import { useEffect, useState } from 'react'
import { useOtherAvailabilitySectionBaseSpacing } from '@/Reservations/Search/OtherAvailability'
import { OtherAvailabilitySection } from '@/Reservations/Search/OtherAvailability/OtherAvailabilitySection'
import { useLocales } from '@sevenrooms/locales'
import { useMaxWidthBreakpoint } from '@sevenrooms/ui-kit/hooks'
import { Box } from '@sevenrooms/ui-kit/layout'
import { Text } from '@sevenrooms/ui-kit/typography'
import {
  type AvailabilityTimeWithUpSellCost,
  useReservationsRoute,
  type ExperienceWithAvailability,
  useOtherVenueNavigation,
} from '../../hooks'
import { reservationWidgetMessages } from '../../reservationWidgetMessages'
import { OtherVenueAvailability } from './OtherAvailability'
import type { OtherVenueInfo } from '@/domain'

export interface OtherVenuesAvailabilityProps {
  title: string
  collapsed: boolean
  setCollapsed: (collapsed: boolean) => void
  otherVenues: OtherVenueInfo[]
  startDate: string
  startTime: string
  haloTimeIntervalMinutes: number
  defaultSearchTime: string
  partySize: number
  duration?: number
  selectedLangCode?: string
  startOfDayTime: string
  currencyCode: string
  clientId?: string
  onTimeSlotClick: (slot: AvailabilityTimeWithUpSellCost, venue: OtherVenueInfo) => void
  showPrivateEvents?: boolean
  onPrivateEventsTimeSlotClick?: (
    slot: AvailabilityTimeWithUpSellCost,
    experience: ExperienceWithAvailability,
    venue: OtherVenueInfo
  ) => void
  onPrivateEventsMoreDetailsClick?: (experience: ExperienceWithAvailability) => void
}

export function OtherVenuesAvailability({
  title,
  collapsed = true,
  setCollapsed,
  otherVenues,
  startDate,
  startTime,
  defaultSearchTime,
  haloTimeIntervalMinutes,
  partySize,
  duration,
  selectedLangCode = 'en',
  startOfDayTime,
  currencyCode,
  onTimeSlotClick,
  clientId,
  showPrivateEvents,
  onPrivateEventsTimeSlotClick,
  onPrivateEventsMoreDetailsClick,
}: OtherVenuesAvailabilityProps) {
  const { navigateToOtherVenue } = useOtherVenueNavigation()
  const { primaryVenueId } = useReservationsRoute()
  const { formatMessage } = useLocales()
  const [venuesWithNoAvailabilities, setVenuesWithNoAvailabilities] = useState<string[]>([])
  const isMobile = useMaxWidthBreakpoint('s')
  const baseSpacing = useOtherAvailabilitySectionBaseSpacing()

  useEffect(() => {
    setVenuesWithNoAvailabilities([])
  }, [startTime, startDate, partySize, haloTimeIntervalMinutes])

  return (
    <Box pt="lm">
      <OtherAvailabilitySection title={title} collapsed={collapsed} setCollapsed={setCollapsed} data-test="other-venues-availability">
        {otherVenues.length > 0 && venuesWithNoAvailabilities.length === otherVenues.length ? (
          <Box p="sm" pb="lm">
            <Text textStyle="body1">{formatMessage(reservationWidgetMessages.resWidgetOtherLocationsNoAvail)}</Text>
          </Box>
        ) : (
          <>
            {otherVenues.map(v => (
              <OtherVenueAvailability
                key={v.urlKey}
                venue={v}
                partySize={partySize}
                startTime={startTime}
                startDate={startDate}
                defaultSearchTime={defaultSearchTime}
                haloTimeIntervalMinutes={haloTimeIntervalMinutes}
                duration={duration}
                clientId={clientId}
                currencyCode={currencyCode}
                selectedLangCode={selectedLangCode}
                startOfDayTime={startOfDayTime}
                collapsed={collapsed}
                isMobile={isMobile}
                isGroupWidget={false}
                onTimeSlotClick={onTimeSlotClick}
                onTitleClick={navigateToOtherVenue}
                noAvailabilityMessage={formatMessage(reservationWidgetMessages.resWidgetNoAdditionalAvail)}
                onNoAvailabilities={(venueId: string) => {
                  if (!venuesWithNoAvailabilities.includes(venueId)) {
                    setVenuesWithNoAvailabilities([...venuesWithNoAvailabilities, venueId])
                  }
                }}
                primaryVenueId={primaryVenueId}
                showPrivateEvents={showPrivateEvents}
                onPrivateEventsTimeSlotClick={(slot: AvailabilityTimeWithUpSellCost, experience: ExperienceWithAvailability) =>
                  onPrivateEventsTimeSlotClick?.(slot, experience, v)
                }
                onPrivateEventsMoreDetailsClick={onPrivateEventsMoreDetailsClick}
                onShowMoreClick={() => navigateToOtherVenue(v.urlKey)}
                scrollRightMargin={baseSpacing}
              />
            ))}
          </>
        )}
      </OtherAvailabilitySection>
    </Box>
  )
}
